import React, { useState, useEffect, useCallback, useRef } from "react";
import { CrudPage } from "@/components/CrudPage";
import { StatusBadge } from "@/components/StatusBadge";
import { CONTRACT_STATUS } from "@/lib/constants";
import { FileText } from "lucide-react";
import { PortalProvider } from "@/context/PortalContext";
import { ProfilePhoto, IdDocument } from "@/components/PhotoCapture";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

const STATUS_OPTS = Object.entries(CONTRACT_STATUS).map(([value, v]) => ({ value, label: v.label }));

const columns = [
  { key: "contract_number", label: "契約番号", render: (r) => <span className="font-medium">{r.contract_number}</span> },
  { key: "property_id", label: "物件", render: (r, h) => h.nameOf("properties", r.property_id) },
  { key: "room_id", label: "部屋", render: (r, h) => h.nameOf("rooms", r.room_id) },
  { key: "tenant_id", label: "入居者", render: (r, h) => h.nameOf("tenants", r.tenant_id) },
  { key: "start_date", label: "契約期間", render: (r, h) => `${h.dateJP(r.start_date)} 〜 ${h.dateJP(r.end_date)}` },
  { key: "rent", label: "賃料", render: (r, h) => h.yen(r.rent) },
  { key: "status", label: "状態", render: (r) => <StatusBadge value={r.status} map={CONTRACT_STATUS} /> },
];

// 契約新規登録時の借主証明（顔写真・身分証明書・外国籍は在留カード）。撮影内容は借主プロフィールにも反映。
function ContractDocuments({ values, setVal }) {
  const tid = values.tenant_id;
  const loadedRef = useRef(null);
  useEffect(() => {
    if (!tid || loadedRef.current === tid) return;
    loadedRef.current = tid;
    api.get(`/tenant-admin/tenants/${tid}/profile`).then(({ data }) => {
      setVal("nationality", data.nationality || "");
      setVal("photo", data.photo || "");
      setVal("id_doc_type", data.id_doc_type || "residence_card");
      setVal("doc_identity_front", data.doc_identity_front || "");
      setVal("doc_identity_back", data.doc_identity_back || "");
      setVal("doc_residence_front", data.doc_residence_front || "");
      setVal("doc_residence_back", data.doc_residence_back || "");
    }).catch(() => {});
  }, [tid, setVal]);

  if (!tid) {
    return (
      <div className="rounded-md border border-dashed border-input p-4 text-sm text-muted-foreground" data-testid="contract-docs-empty">
        先に「入居者」を選択すると、本人確認書類（顔写真・身分証明書・在留カード）の撮影欄が表示されます。
      </div>
    );
  }
  const foreign = values.nationality && values.nationality !== "日本";
  return (
    <PortalProvider value={`/tenant-admin/tenants/${tid}`}>
      <div className="space-y-5 rounded-md bg-[#111111] p-4" data-testid="contract-docs">
        <div>
          <h4 className="text-sm font-medium text-white mb-1">本人確認書類（任意）</h4>
          <p className="text-xs text-gray-400">
            借主の顔写真・身分証明書{foreign ? "・在留カード" : ""}を撮影できます。未撮影・不備があっても契約は保存できます（後からプロフィールで追加可能）。
          </p>
        </div>
        <ProfilePhoto label="顔写真" value={values.photo} onChange={(v) => setVal("photo", v)} testid="contract-face" />
        <div>
          <div className="text-sm text-white mb-1.5">身分証明書</div>
          <IdDocument
            docType={values.id_doc_type || "residence_card"}
            front={values.doc_identity_front}
            back={values.doc_identity_back}
            onDocType={(v) => setVal("id_doc_type", v)}
            onFront={(v) => setVal("doc_identity_front", v)}
            onBack={(v) => setVal("doc_identity_back", v)}
            testidPrefix="contract-id"
          />
        </div>
        {foreign && (
          <div>
            <div className="text-sm text-white mb-1.5">在留カード</div>
            <IdDocument
              lockType
              docType="residence_card"
              front={values.doc_residence_front}
              back={values.doc_residence_back}
              onDocType={() => {}}
              onFront={(v) => setVal("doc_residence_front", v)}
              onBack={(v) => setVal("doc_residence_back", v)}
              testidPrefix="contract-residence"
            />
          </div>
        )}
      </div>
    </PortalProvider>
  );
}

const baseFields = [
  { name: "contract_number", label: "契約番号", placeholder: "空欄で自動採番", help: "自動入力されます（編集可）" },
  { name: "property_id", label: "対象物件", type: "select", lookup: "properties", required: true },
  { name: "room_id", label: "対象部屋", type: "select", lookup: "rooms" },
  { name: "tenant_id", label: "入居者", type: "select", lookup: "tenants", required: true },
  { name: "start_date", label: "契約開始日", type: "date" },
  { name: "end_date", label: "契約終了日", type: "date" },
  { name: "rent", label: "賃料", type: "currency" },
  { name: "common_fee", label: "共益費", type: "currency" },
  { name: "management_fee", label: "管理費", type: "currency" },
  { name: "parking_fee", label: "駐車場料金", type: "currency" },
  { name: "other_fee", label: "その他費用", type: "currency" },
  { name: "deposit", label: "敷金", type: "currency" },
  { name: "key_money", label: "礼金", type: "currency" },
  { name: "renewal_fee", label: "更新料", type: "currency" },
  { name: "pay_due", label: "家賃支払期日", type: "select", options: [
    { value: "毎月末日", label: "毎月末日" },
    { value: "毎月1日", label: "毎月1日" },
    { value: "毎月5日", label: "毎月5日" },
    { value: "毎月10日", label: "毎月10日" },
    { value: "毎月15日", label: "毎月15日" },
    { value: "毎月20日", label: "毎月20日" },
    { value: "毎月25日", label: "毎月25日" },
    { value: "毎月27日", label: "毎月27日" },
    { value: "前月末日", label: "前月末日（前払い）" },
  ] },
  { name: "pay_method", label: "支払方法", type: "select", options: [
    { value: "銀行振込", label: "銀行振込" },
    { value: "口座振替", label: "口座振替" },
    { value: "その他", label: "その他" },
  ] },
  { name: "guarantee_company", label: "保証会社" },
  { name: "status", label: "契約ステータス", type: "select", options: STATUS_OPTS, default: "active" },
  { name: "note", label: "特記事項", type: "textarea" },
];

const DOC_HIDDEN = ["photo", "id_doc_type", "doc_identity_front", "doc_identity_back", "doc_residence_front", "doc_residence_back", "nationality"];

const docField = {
  name: "_docs", type: "custom", colSpan: 2,
  render: (ctx) => <ContractDocuments {...ctx} />,
};

export default function Contracts() {
  const [nextNo, setNextNo] = useState("");
  const [agrMap, setAgrMap] = useState({});

  const refreshNo = useCallback(async () => {
    try {
      const { data } = await api.get("/contracts/next-number");
      setNextNo(data.contract_number || "");
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { refreshNo(); }, [refreshNo]);

  useEffect(() => {
    api.get("/tenant-admin/agreements")
      .then(({ data }) => {
        const m = {};
        (data || []).forEach((a) => { m[a.contract_id] = a.status; });
        setAgrMap(m);
      })
      .catch(() => {});
  }, []);

  const fields = [
    ...baseFields.map((f) => (f.name === "contract_number" ? { ...f, default: nextNo } : f)),
    docField,
    ...DOC_HIDDEN.map((name) => ({ name, type: "hidden" })),
  ];

  // 撮影した証明を借主プロフィールへ反映（契約側にも hidden で保存される）
  const beforeSubmit = useCallback(async (v) => {
    if (!v.tenant_id) return;
    try {
      await api.put(`/tenant-admin/tenants/${v.tenant_id}/profile`, {
        photo: v.photo || "",
        id_doc_type: v.id_doc_type || "residence_card",
        doc_identity_front: v.doc_identity_front || "",
        doc_identity_back: v.doc_identity_back || "",
        doc_residence_front: v.doc_residence_front || "",
        doc_residence_back: v.doc_residence_back || "",
      });
    } catch { toast.warning("借主プロフィールへの証明反映に失敗しました（契約は登録されます）"); }
  }, []);

  const cols = [
    ...columns,
    {
      key: "sign_status", label: "サイン状況",
      render: (r) => {
        const s = agrMap[r.id];
        const cfg = s === "approved" ? { t: "承認済み", c: "bg-green-100 text-green-700" }
          : s === "submitted" ? { t: "サイン済み", c: "bg-blue-100 text-blue-700" }
          : { t: "未サイン", c: "bg-gray-100 text-gray-500" };
        return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.c}`} data-testid={`sign-status-${r.id}`}>{cfg.t}</span>;
      },
    },
    {
      key: "agreement", label: "書類",
      render: (r) => (
        <div className="flex flex-col gap-0.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => window.open(`${API}/tenant-admin/contracts/${r.id}/important-matters-pdf`, "_blank", "noopener")} className="inline-flex items-center gap-1 text-brand hover:underline text-xs" data-testid={`imed-pdf-${r.id}`}><FileText className="w-3 h-3" />重要事項説明書</button>
          <button onClick={() => window.open(`${API}/tenant-admin/contracts/${r.id}/lease-pdf`, "_blank", "noopener")} className="inline-flex items-center gap-1 text-brand hover:underline text-xs" data-testid={`lease-pdf-${r.id}`}><FileText className="w-3 h-3" />賃貸借契約書</button>
          {(agrMap[r.id] === "submitted" || agrMap[r.id] === "approved") && <button onClick={() => window.open(`${API}/tenant-admin/agreements/${r.id}/pdf`, "_blank", "noopener")} className="inline-flex items-center gap-1 text-brand hover:underline text-xs" data-testid={`contract-pdf-${r.id}`}><FileText className="w-3 h-3" />署名済契約書</button>}
        </div>
      ),
    },
  ];

  return (
    <CrudPage
      title="契約管理"
      subtitle="賃貸借契約の一覧・更新管理"
      endpoint="/contracts"
      resource="contracts"
      columns={cols}
      fields={fields}
      testid="contracts"
      addLabel="契約を登録"
      searchKeys={["contract_number"]}
      onSaved={refreshNo}
      beforeSubmit={beforeSubmit}
    />
  );
}
